import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  SoundCategory,
  PracticeText,
  UserPronunciationProfile,
  getSoundCategories,
  getPracticeTexts,
  getRandomPracticeText,
  getUserPronunciationProfile,
  updatePronunciationSession,
} from '../services/api';

export type PracticeSoundsMode = 'mode_select' | 'category_select' | 'text_select' | 'practice' | 'processing' | 'results';

interface UsePracticeSoundsReturn {
  // State
  mode: PracticeSoundsMode;
  categories: SoundCategory[];
  selectedCategory: SoundCategory | null;
  selectedText: PracticeText | null;
  availableTexts: PracticeText[];
  userProfile: UserPronunciationProfile | null;
  recommendedCategory: string | null;
  practiceType: 'specific' | 'random' | null;

  // Loading states
  loadingCategories: boolean;
  loadingTexts: boolean;
  loadingProfile: boolean;
  error: string | null;

  // Actions
  setMode: (mode: PracticeSoundsMode) => void;
  selectPracticeType: (type: 'specific' | 'random') => void;
  selectCategory: (category: SoundCategory) => void;
  selectText: (text: PracticeText) => void;
  loadTextsForCategory: (categoryId: string) => Promise<void>;
  getRandomText: (categoryId?: string) => Promise<void>;
  shuffleTexts: () => Promise<void>;
  updateSessionResults: (score: number, phonemeAnalysis?: { phoneme: string; accuracy: number; word: string }[]) => Promise<void>;
  reset: () => void;
  goBack: () => void;
}

export const usePracticeSounds = (): UsePracticeSoundsReturn => {
  const { currentUser } = useAuth();

  // Mode state
  const [mode, setMode] = useState<PracticeSoundsMode>('mode_select');
  const [practiceType, setPracticeType] = useState<'specific' | 'random' | null>(null);

  // Data state
  const [categories, setCategories] = useState<SoundCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SoundCategory | null>(null);
  const [selectedText, setSelectedText] = useState<PracticeText | null>(null);
  const [availableTexts, setAvailableTexts] = useState<PracticeText[]>([]);
  const [userProfile, setUserProfile] = useState<UserPronunciationProfile | null>(null);

  // Loading states
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingTexts, setLoadingTexts] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate recommended category based on user's problem sounds
  const recommendedCategory = userProfile?.problemSounds?.[0]?.category || null;

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      setError(null);
      try {
        const data = await getSoundCategories();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError('Failed to load sound categories');
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Load user profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser?.uid) return;

      setLoadingProfile(true);
      try {
        const profile = await getUserPronunciationProfile(currentUser.uid);
        setUserProfile(profile);
      } catch (err) {
        // Profile might not exist yet - that's okay
        console.log('No profile found or error loading:', err);
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfile();
  }, [currentUser?.uid]);

  // Load texts for a category
  const loadTextsForCategory = useCallback(async (categoryId: string) => {
    setLoadingTexts(true);
    setError(null);
    try {
      const result = await getPracticeTexts([categoryId], 3);
      setAvailableTexts(result.texts);
    } catch (err) {
      console.error('Failed to load texts:', err);
      setError('Failed to load practice texts');
    } finally {
      setLoadingTexts(false);
    }
  }, []);

  // Get a random text (optionally for a category)
  const getRandomText = useCallback(async (categoryId?: string) => {
    setLoadingTexts(true);
    setError(null);
    try {
      const text = await getRandomPracticeText(categoryId);
      setSelectedText(text);
      setAvailableTexts([text]);
      setMode('practice');
    } catch (err) {
      console.error('Failed to get random text:', err);
      setError('Failed to get practice text');
    } finally {
      setLoadingTexts(false);
    }
  }, []);

  // Shuffle texts (reload different texts for the category)
  const shuffleTexts = useCallback(async () => {
    if (selectedCategory) {
      await loadTextsForCategory(selectedCategory.id);
    }
  }, [selectedCategory, loadTextsForCategory]);

  // Select practice type (specific sound vs random)
  const selectPracticeType = useCallback((type: 'specific' | 'random') => {
    setPracticeType(type);
    if (type === 'specific') {
      setMode('category_select');
    } else {
      // Random mode - get a random text right away
      getRandomText();
    }
  }, [getRandomText]);

  // Select a category
  const selectCategory = useCallback((category: SoundCategory) => {
    setSelectedCategory(category);
    loadTextsForCategory(category.id);
    setMode('text_select');
  }, [loadTextsForCategory]);

  // Select a text
  const selectText = useCallback((text: PracticeText) => {
    setSelectedText(text);
    setMode('practice');
  }, []);

  // Update session results after practice
  const updateSessionResults = useCallback(async (
    score: number,
    phonemeAnalysis?: { phoneme: string; accuracy: number; word: string }[]
  ) => {
    if (!currentUser?.uid) return;

    try {
      const updatedProfile = await updatePronunciationSession(
        currentUser.uid,
        score,
        phonemeAnalysis
      );
      setUserProfile(updatedProfile);
    } catch (err) {
      console.error('Failed to update session:', err);
      // Don't show error to user - this is a background operation
    }
  }, [currentUser?.uid]);

  // Reset to initial state
  const reset = useCallback(() => {
    setMode('mode_select');
    setPracticeType(null);
    setSelectedCategory(null);
    setSelectedText(null);
    setAvailableTexts([]);
    setError(null);
  }, []);

  // Go back one step
  const goBack = useCallback(() => {
    switch (mode) {
      case 'category_select':
        setMode('mode_select');
        setPracticeType(null);
        break;
      case 'text_select':
        setMode('category_select');
        setSelectedCategory(null);
        setAvailableTexts([]);
        break;
      case 'practice':
        if (practiceType === 'random') {
          setMode('mode_select');
          setPracticeType(null);
        } else {
          setMode('text_select');
        }
        setSelectedText(null);
        break;
      case 'results':
        setMode('practice');
        break;
      default:
        break;
    }
  }, [mode, practiceType]);

  return {
    // State
    mode,
    categories,
    selectedCategory,
    selectedText,
    availableTexts,
    userProfile,
    recommendedCategory,
    practiceType,

    // Loading states
    loadingCategories,
    loadingTexts,
    loadingProfile,
    error,

    // Actions
    setMode,
    selectPracticeType,
    selectCategory,
    selectText,
    loadTextsForCategory,
    getRandomText,
    shuffleTexts,
    updateSessionResults,
    reset,
    goBack,
  };
};

export default usePracticeSounds;
